import {
  filter,
  flatMap,
  groupBy,
  isPlainObject,
  last,
  map,
  pipe,
  sortBy
} from 'remeda'
import { NG_PREFIX } from './export/null-safe-group-by'

export type ReportLinkTemplates = Readonly<Record<string, string>>
export type ReportLinksConfig = ReportLinkTemplates | readonly string[]

export type LinkContextScalar = string | number | boolean
export type LinkContextSplitPath = readonly string[]
export type LinkContext = Record<string, string>
export type LinkTemplateContextValue =
  | string
  | readonly string[]
  | null
  | undefined
export type LinkTemplateContext = Readonly<
  Record<string, LinkTemplateContextValue>
>
export type ReportRowClickPayload = {
  readonly data: {
    readonly column_names: readonly [string]
    readonly row: LinkContext
  }
}

export type LinkContextCell = {
  readonly field: string
  readonly splitPath: LinkContextSplitPath
  readonly value: LinkContextScalar
}

type MustacheToken = readonly [string, string, ...(readonly unknown[])]

type LinkTemplateRenderer = Pick<
  typeof import('mustache')['default'],
  'parse' | 'render'
>

const isDefined = <T>(value: T | undefined): value is T => value !== undefined

const rawPerspectiveField = (field: string): string =>
  field.startsWith(NG_PREFIX) ? field.slice(NG_PREFIX.length) : field

type ParsedLinkContextEntry = {
  readonly field: string
  readonly splitPath: LinkContextSplitPath
  readonly value: LinkContextScalar
}

const isRenderableLinkContextCell = (cell: LinkContextCell): boolean =>
  cell.value !== '' && cell.value !== 'null'

const parseLinkContextCell = (
  cell: LinkContextCell
): ParsedLinkContextEntry => ({
  field: rawPerspectiveField(cell.field),
  splitPath: cell.splitPath,
  value: cell.value
})

const hasMatchingSplitPath = (
  candidate: ParsedLinkContextEntry,
  splitPath: LinkContextSplitPath
): boolean =>
  candidate.splitPath.length <= splitPath.length &&
  candidate.splitPath.every((segment, index) => segment === splitPath[index])

const selectLinkContextEntry = (
  candidates: readonly ParsedLinkContextEntry[],
  splitPath: LinkContextSplitPath
): ParsedLinkContextEntry | undefined => {
  const matching = candidates.filter(
    (candidate) =>
      splitPath.length === 0 || hasMatchingSplitPath(candidate, splitPath)
  )
  const first = matching[0]

  return first && matching.every((candidate) => candidate.value === first.value)
    ? first
    : undefined
}

/** Empty split buckets carry no row; populated buckets with null IDs stay ambiguous. */
const linkContextCellsFromRow = (
  row: Readonly<Record<string, unknown>>,
  idColumns: readonly string[]
): readonly LinkContextCell[] => {
  const entries = Object.entries(row)
  const populatedPaths = new Set(
    entries
      .filter(([_key, value]) => value !== null)
      .map(([key]) => key.split('|').slice(0, -1).join('|'))
  )

  return entries.flatMap(([path, value]): readonly LinkContextCell[] => {
    const header = path.split('|')
    const field = last(header)
    const scalar =
      typeof value === 'string' ||
      typeof value === 'boolean' ||
      (typeof value === 'number' && Number.isFinite(value))

    return field &&
      idColumns.includes(field) &&
      populatedPaths.has(header.slice(0, -1).join('|'))
      ? [
          {
            field,
            value: scalar ? value : '',
            splitPath: header.length > 1 ? [header.slice(0, -1).join('|')] : []
          }
        ]
      : []
  })
}

const groupLinkContextEntries = (
  entries: readonly ParsedLinkContextEntry[]
): Readonly<Record<string, readonly ParsedLinkContextEntry[]>> =>
  groupBy(entries, (entry) => entry.field)

const isMustacheToken = (token: unknown): token is MustacheToken =>
  Array.isArray(token) &&
  typeof token[0] === 'string' &&
  typeof token[1] === 'string'

const tokenChildren = (token: MustacheToken): readonly MustacheToken[] =>
  Array.isArray(token[4]) ? token[4].filter(isMustacheToken) : []

const tokenField = (token: MustacheToken): string | undefined =>
  token[0] === 'name' || token[0] === '&' ? token[1] : undefined

const hasLinkTemplateValue = (value: LinkTemplateContextValue): boolean =>
  Array.isArray(value) ? value.length > 0 : value !== undefined && value !== ''

const urlParamContextKey = (key: string): string | undefined =>
  key.includes('[') ? key.split('[')?.[1]?.split(']')?.[0] : key || undefined

const equalityFilterBaseKey = (key: string): string | undefined =>
  key.endsWith('_eq') ? key.slice(0, -3) : undefined

const urlParamValue = (
  params: URLSearchParams,
  key: string
): string | readonly string[] => {
  const values = params.getAll(key)

  return values.length > 1 ? values : (params.get(key) ?? '')
}

const splitOnce = (
  value: string,
  separator: string
): readonly [string, string | undefined] => {
  const index = value.indexOf(separator)

  return index === -1
    ? [value, undefined]
    : [value.slice(0, index), value.slice(index + separator.length)]
}

const isNonEmptyQueryParam = (param: string): boolean => {
  const [_key, value] = splitOnce(param, '=')

  return value !== undefined && value !== ''
}

export const normalizeReportLinks = (
  links: ReportLinksConfig
): ReportLinkTemplates =>
  Array.isArray(links) ? {} : (links as ReportLinkTemplates)

export const urlTemplateContextFromSearch = (
  search: string
): LinkTemplateContext => {
  const params = new URLSearchParams(search)
  const exactEntries = pipe(
    Array.from(new Set(params.keys())),
    map((key): readonly [string, LinkTemplateContextValue] | undefined => {
      const contextKey = urlParamContextKey(key)

      return contextKey ? [contextKey, urlParamValue(params, key)] : undefined
    }),
    filter(isDefined)
  )
  const exactContext = Object.fromEntries(exactEntries)
  const equalityFallbackEntries = pipe(
    exactEntries,
    flatMap(([key, value]) => {
      const baseKey = equalityFilterBaseKey(key)

      return baseKey && exactContext[baseKey] === undefined
        ? ([[baseKey, value]] as const)
        : []
    })
  )

  return Object.fromEntries([...equalityFallbackEntries, ...exactEntries])
}

export const linkTemplateContextFromRowAndSearch = (
  row: LinkContext,
  search: string
): LinkTemplateContext => ({
  ...urlTemplateContextFromSearch(search),
  ...row
})

export const linkTemplatePath = (template: string): string =>
  splitOnce(template, '?')[0]

export const pruneEmptyQueryParams = (href: string): string => {
  const [hrefWithoutHash, hash] = splitOnce(href, '#')
  const [path, query] = splitOnce(hrefWithoutHash, '?')

  if (query === undefined) {
    return href
  }

  const prunedQuery = pipe(query.split('&'), filter(isNonEmptyQueryParam))

  return [
    path,
    prunedQuery.length > 0 ? `?${prunedQuery.join('&')}` : '',
    hash === undefined ? '' : `#${hash}`
  ].join('')
}

/** Keep modified-click report navigation consistent with LiveView's currency filter. */
export const reportLinkWithCurrency = (
  href: string,
  search: string
): string => {
  const currency = new URLSearchParams(search).get('filter[currency]')
  const url = new URL(href, 'https://report.invalid')
  const report =
    href.startsWith('/reports/') && !url.searchParams.has('filter[currency]')

  if (report && currency) {
    url.searchParams.set('filter[currency]', currency)
  }

  return report && currency ? `${url.pathname}${url.search}${url.hash}` : href
}

export const linkFieldForHeader = (
  links: ReportLinkTemplates,
  header: readonly string[]
): string | undefined => {
  const linkColumns = Object.keys(links)

  return pipe(
    header,
    map(rawPerspectiveField),
    filter((field) => linkColumns.includes(field)),
    last()
  )
}

export const linkFieldForReportCell = ({
  links,
  columnHeader,
  groupBy,
  rowHeaderDepth
}: {
  readonly links: ReportLinkTemplates
  readonly columnHeader: readonly string[]
  readonly groupBy: readonly string[]
  readonly rowHeaderDepth?: number
}): string | undefined => {
  if (rowHeaderDepth !== undefined) {
    const rowHeaderField = groupBy[Math.min(rowHeaderDepth, groupBy.length - 1)]

    return rowHeaderField
      ? linkFieldForHeader(links, [rowHeaderField])
      : undefined
  }

  return (
    linkFieldForHeader(links, columnHeader) ??
    linkFieldForHeader(links, [...groupBy, ...columnHeader])
  )
}

export const linkContextFromCells = (
  cells: readonly LinkContextCell[],
  splitPath: LinkContextSplitPath = []
): LinkContext =>
  Object.fromEntries(
    Object.entries(groupLinkContextEntries(cells.map(parseLinkContextCell)))
      .map(([field, candidates]) => {
        const selected = selectLinkContextEntry(candidates, splitPath)

        return selected && isRenderableLinkContextCell(selected)
          ? [field, String(selected.value)]
          : undefined
      })
      .filter(isDefined)
  )

/** Read raw carrier values independently of the grid's rendered column window. */
export const linkContextFromRow = (
  row: unknown,
  idColumns: readonly string[],
  splitPath: LinkContextSplitPath
): LinkContext | undefined =>
  isPlainObject(row)
    ? linkContextFromCells(
        linkContextCellsFromRow(row, idColumns),
        splitPath.length > 0 ? [splitPath.join('|')] : []
      )
    : undefined

export const mustacheTemplateFields = (
  tokens: readonly unknown[]
): readonly string[] =>
  Array.from(
    new Set(
      tokens
        .filter(isMustacheToken)
        .flatMap((token) => [
          tokenField(token),
          ...mustacheTemplateFields(tokenChildren(token))
        ])
        .filter(isDefined)
    )
  )

/** Keep only the ordered carriers needed by links on visible or grouped fields. */
export const reportLinkColumns = (
  columns: readonly (string | null | undefined)[] | undefined,
  groupBy: readonly string[],
  idColumns: readonly string[],
  links: ReportLinkTemplates,
  renderer: Pick<LinkTemplateRenderer, 'parse'>
): {
  readonly columns: string[]
  readonly hiddenColumns: readonly string[]
} => {
  const visible = (columns ?? []).filter(
    (column): column is string =>
      typeof column === 'string' && !idColumns.includes(column)
  )
  const dependencies = new Set(
    [...visible, ...groupBy].flatMap((field) => {
      const template = links[rawPerspectiveField(field)]

      return template ? mustacheTemplateFields(renderer.parse(template)) : []
    })
  )
  const hiddenColumns = idColumns.filter((field) => dependencies.has(field))

  return {
    columns: [...new Set([...hiddenColumns, ...visible])],
    hiddenColumns
  }
}

export const rowLinkTemplateFields = (
  fields: readonly string[],
  idColumns: readonly string[]
): readonly string[] => fields.filter((field) => idColumns.includes(field))

export const hasRequiredLinkContext = (
  context: LinkTemplateContext,
  fields: readonly string[]
): boolean =>
  fields.every((field) => field === '.' || hasLinkTemplateValue(context[field]))

/** Render only links with a current row and all required path values. */
export const renderReportLink = (
  template: string | undefined,
  row: LinkContext | undefined,
  idColumns: readonly string[],
  search: string,
  renderer: LinkTemplateRenderer
): string | undefined => {
  const context = linkTemplateContextFromRowAndSearch(row ?? {}, search)
  const required = mustacheTemplateFields(
    renderer.parse(linkTemplatePath(template ?? ''))
  )
  const valid =
    template &&
    row &&
    Object.keys(row).length > 0 &&
    hasRequiredLinkContext(row, rowLinkTemplateFields(required, idColumns)) &&
    hasRequiredLinkContext(context, required)

  return valid
    ? reportLinkWithCurrency(
        pruneEmptyQueryParams(renderer.render(template, context)),
        search
      )
    : undefined
}

export const reportRowClickPayload = (
  field: string,
  row: LinkContext
): ReportRowClickPayload => ({
  data: {
    column_names: [field],
    row
  }
})

export const reportRowClickPayloadSignature = (
  payload: ReportRowClickPayload
): string =>
  JSON.stringify({
    column_names: payload.data.column_names,
    row: pipe(
      Object.entries(payload.data.row),
      sortBy(([field]) => field)
    )
  })
